<script setup lang="ts">
import type { HoverCardRequest } from '#layers/ui/composables/hover-card-context';
import { useTimeoutFn } from '@vueuse/core';
import { computed, ref, shallowRef } from 'vue';
import { provideHoverCardController, provideHoverCardOutletContext } from '#layers/ui/composables/hover-card-context';

/**
 * Owns the app's single hover card; `DHoverCardOutlet` renders it. Wrap `DApp` so triggers in
 * programmatic overlays, which Nuxt UI renders beside `DApp`'s slot, see this provider too.
 */

// Long enough to cross the gap onto the card or to a neighbouring trigger, short enough to feel immediate.
const CLOSE_GRACE_MS = 200;

const active = shallowRef<HoverCardRequest>();
const open = ref(false);
const openReference = computed(() => open.value ? active.value?.reference : undefined);
const { start: scheduleClose, stop: cancelClose } = useTimeoutFn(() => {
	open.value = false;
}, CLOSE_GRACE_MS, { immediate: false });

// Keeps `active`, so the outlet's popover stays mounted and the next trigger repositions it instead of
// remounting it; `release` clears it once its trigger unmounts.
function close(): void {
	cancelClose();
	open.value = false;
}

function isActive(reference: HTMLElement): boolean {
	return active.value?.reference === reference;
}

// Moving between triggers swaps the anchor and content in one update while the card stays open,
// so it repositions instead of closing and reopening.
provideHoverCardController({
	openReference,
	show(request) {
		cancelClose();
		active.value = request;
		open.value = true;
	},
	hide(reference) {
		if (isActive(reference)) scheduleClose();
	},
	release(reference) {
		if (!isActive(reference)) return;
		close();
		active.value = undefined;
	},
});

provideHoverCardOutletContext({
	active: computed(() => active.value),
	open: computed(() => open.value),
	openReference,
	keepOpen: cancelClose,
	closeSoon: scheduleClose,
	close,
});
</script>

<template>
	<slot />
</template>
